import React, { useEffect, useRef, useState } from "react";
import { RoleHelper } from "src/helpers";
import {
  Note,
  UpdatePurchaseOrderOpsNoteParams,
  PurchaseOrder,
  WarehouseCheckIn,
  UpdateWarehouseCheckInOpsNoteParams
} from "src/types";
import { useAuthorisation } from "src/context";
import { IconButton, NoteDisplay } from "src/components";
import { TextInputBoxLarge } from "src/components/ui/inputs";

interface EditOpsNoteProps<T> {
  id: string;
  timestamp: T extends WarehouseCheckIn ? string : undefined;
  opsNote?: Note;
  onUpdateOpsNote?: (
    params: T extends WarehouseCheckIn ? UpdateWarehouseCheckInOpsNoteParams : UpdatePurchaseOrderOpsNoteParams
  ) => void;
  isTextButton?: boolean;
  textTruncateLength?: number;
}

const EditOpsNote = <T extends PurchaseOrder | WarehouseCheckIn>({
  id,
  timestamp,
  opsNote,
  onUpdateOpsNote,
  isTextButton,
  textTruncateLength = 50
}: EditOpsNoteProps<T>) => {
  const { userRole } = useAuthorisation();
  const opsNoteRef = useRef<HTMLDivElement>(null);

  const [textAreaDisplayed, setTextAreaDisplayed] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");

  const isEditable = RoleHelper.isHealfOperationsTeam(userRole) && onUpdateOpsNote;

  const handleSubmit = async (operation: "Update" | "Remove" | "Cancel") => {
    if (operation === "Cancel") {
      return;
    }

    const opsNoteValue = operation === "Update" ? inputValue.trim() : null;

    const updateParams = {
      id,
      opsNote: opsNoteValue,
      ...(timestamp ? { timestamp } : {})
    };

    if (onUpdateOpsNote) {
      onUpdateOpsNote(
        updateParams as T extends WarehouseCheckIn
          ? UpdateWarehouseCheckInOpsNoteParams
          : UpdatePurchaseOrderOpsNoteParams
      );
    }
  };

  const handleOpenEdit = () => {
    if (!isEditable) {
      return;
    }

    if (opsNote?.body) {
      setInputValue(opsNote?.body);
    }
    setTextAreaDisplayed(true);
  };

  const handleCloseEdit = () => {
    setTextAreaDisplayed(false);
    setInputValue("");
  };

  const handleUpdate = () => {
    if (inputValue.trim()) {
      handleSubmit("Update");
      handleCloseEdit();
    }
  };

  const handleCancelOrRemove = () => {
    const operation = inputValue.trim() === opsNote?.body?.trim() ? "Remove" : "Cancel";
    handleSubmit(operation);
    handleCloseEdit();
  };

  useEffect(() => {
    if (!textAreaDisplayed) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (opsNoteRef.current && !opsNoteRef.current.contains(event.target as Node)) {
        handleCloseEdit();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [textAreaDisplayed]);

  if (textAreaDisplayed && onUpdateOpsNote) {
    return (
      <div ref={opsNoteRef} className="w-full min-w-[200px] max-w-2xl">
        <TextInputBoxLarge
          id="ops-note-input"
          value={inputValue}
          onChange={(value) => setInputValue(value)}
          placeholder="Enter operations note..."
          maxLength={400}
          rows={4}
        />
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <button
            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
            onClick={handleUpdate}
            disabled={!inputValue.trim() || inputValue.trim() === opsNote?.body?.trim()}
            type="button"
          >
            {opsNote?.body ? "Update" : "Add"}
          </button>
          <button
            className="px-4 py-2 bg-white border border-secondary-200 text-secondary-700 rounded-lg hover:bg-secondary-50 transition-colors text-sm font-medium"
            onClick={handleCancelOrRemove}
            type="button"
          >
            {inputValue.trim() === opsNote?.body?.trim() ? "Remove" : "Cancel"}
          </button>
        </div>
      </div>
    );
  }

  if (opsNote) {
    const truncatedValue =
      opsNote.body.length > textTruncateLength ? `${opsNote.body.slice(0, textTruncateLength)}...` : opsNote.body;
    return (
      <div
        onClick={handleOpenEdit}
        className={`w-full ${isEditable ? "cursor-pointer hover:opacity-80 transition-opacity" : "cursor-not-allowed"}`}
      >
        <NoteDisplay
          note={{ ...opsNote, body: truncatedValue }}
          style={{ cursor: isEditable ? "pointer" : "not-allowed" }}
        />
      </div>
    );
  }

  if (isEditable) {
    return isTextButton ? (
      <button
        onClick={handleOpenEdit}
        type="button"
        className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-200 rounded-lg hover:bg-secondary-50 transition-colors"
      >
        Add Note
      </button>
    ) : (
      <IconButton onClick={handleOpenEdit} icon={"ADD"} />
    );
  }

  return null;
};

export { EditOpsNote };
